import { z } from "zod";
import { OutboundTransportSchema } from "../schemas/EmailRoutingSchema";
const safeText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^\u0000-\u001f\u007f]*$/);
export const SmtpConfigurationSettingsSchema = z
  .object({
    host: z
      .string()
      .trim()
      .toLowerCase()
      .min(1)
      .max(253)
      .regex(
        /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/,
      ),
    port: z.union([z.literal(465), z.literal(587)]),
    user: safeText(320).min(1),
    fromEmail: z.string().trim().email().max(320),
    fromName: safeText(500).optional(),
  })
  .strict();
const revision = z.string().regex(/^[a-f0-9]{64}$/);
const tenantId = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
export const SmtpConfigurationSchema = z
  .object({
    tenantId,
    revision,
    selectedTransport: OutboundTransportSchema,
    configuration: SmtpConfigurationSettingsSchema.extend({
      passwordStored: z.literal(true),
    })
      .strict()
      .nullable(),
  })
  .strict();
export const ConfigureSmtpCommandSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("SAVE"),
      expectedRevision: revision,
      settings: SmtpConfigurationSettingsSchema,
      password: z.discriminatedUnion("action", [
        z.object({ action: z.literal("KEEP") }).strict(),
        z
          .object({
            action: z.literal("REPLACE"),
            value: z.string().min(1).max(4096),
          })
          .strict(),
      ]),
    })
    .strict(),
  z
    .object({ action: z.literal("DISCONNECT"), expectedRevision: revision })
    .strict(),
]);
export const SmtpConfigurationIssueSchema = z
  .object({
    path: z.enum([
      "settings.host",
      "settings.port",
      "settings.user",
      "settings.fromEmail",
      "settings.fromName",
      "password.action",
      "password.value",
      "testEmail",
      "form",
    ]),
    code: z.enum([
      "REQUIRED",
      "INVALID",
      "TOO_LONG",
      "PASSWORD_REQUIRED",
      "PASSWORD_REPLACEMENT_REQUIRED",
    ]),
  })
  .strict();
export const TestSmtpConfigurationCommandSchema = z
  .object({
    testEmail: z.string().trim().email().max(320),
    expectedRevision: revision,
  })
  .strict();
export const SmtpConfigurationTestResultSchema = z
  .object({
    status: z.literal("ACCEPTED"),
    provider: z.literal("SMTP"),
    tenantId,
    revision,
    testEmail: z.string().email().max(320),
    messageId: z.string().min(1).max(500).optional(),
  })
  .strict();
export type SmtpConfigurationSettings = z.infer<
  typeof SmtpConfigurationSettingsSchema
>;
export type SmtpConfiguration = z.infer<typeof SmtpConfigurationSchema>;
export type ConfigureSmtpCommand = z.infer<typeof ConfigureSmtpCommandSchema>;
export type SmtpConfigurationIssue = z.infer<
  typeof SmtpConfigurationIssueSchema
>;
export type TestSmtpConfigurationCommand = z.infer<
  typeof TestSmtpConfigurationCommandSchema
>;
export type SmtpConfigurationTestResult = z.infer<
  typeof SmtpConfigurationTestResultSchema
>;
